import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  ChipPropsVariantOverrides,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import AvatarCircle from "app/components/AvatarCircle";
import {
  LargeCustomIcon,
  MediumCustomIcon,
  SmallCustomIcon,
} from "../../layer00";
import { OneOnOnePrepModal } from "../../layer16";
import { WinSpotlight, IssueSpotlight } from "app/containers/TeamsTab/types";
import { openTeamsChat } from "utils/helperFunctions";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  generateEmployeeCheckInReviewInstructions,
  selectGenerateInstructionsStatus,
  setChatbotMessages,
} from "app/containers/Chatbot/slice";
import { getLetsChatIntroMessage } from "app/containers/Chatbot/helpers";
import { selectCurrentUserInfo } from "app/containers/Global/slice";
import { useNavigate } from "../../../common";

export type ConversationCardProps = {
  userAccountId: number;
  personName: string;
  emailAddress?: string;
  comment: string;
  conversationState: keyof ChipPropsVariantOverrides;
  weeklyPoints: string[];
  winSpotlight: WinSpotlight | null;
  issueSpotlight: IssueSpotlight | null;
  date?: string | null;
};

const label2Variant: Record<keyof ChipPropsVariantOverrides, string> = {
  error: "Needs Attention",
  success: "Win",
  info: "Needs Support",
  due: "Due",
  inProgress: "In Progress",
  onTrack: "On Track",
  reportCount: "Report Count",
  warning: "Warning",
};

export const ConversationCard = ({
  userAccountId,
  personName,
  emailAddress,
  comment,
  conversationState,
  weeklyPoints,
  winSpotlight,
  issueSpotlight,
  date,
}: Readonly<ConversationCardProps>) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const generateStatus = useAppSelector(selectGenerateInstructionsStatus);
  const isGenerating = generateStatus === "loading";
  const currentUserInfo = useAppSelector(selectCurrentUserInfo);

  const handleLetsChat = async () => {
    await dispatch(
      generateEmployeeCheckInReviewInstructions({
        userAccountId,
        weeklyScope: 1,
      })
    );
    dispatch(
      setChatbotMessages([
        {
          role: "assistant",
          content: getLetsChatIntroMessage(
            currentUserInfo?.firstName ?? "",
            personName
          ),
        },
      ])
    );
    navigate("/user/chatWithBo");
  };

  const letsChatButton = (
    <Button
      variant="outlined"
      color="primary"
      startIcon={
        isGenerating ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <LargeCustomIcon icon="SPARKLE" />
        )
      }
      disabled={isGenerating}
      onClick={handleLetsChat}
      sx={{
        width: {
          xs: "100%",
          sm: "auto",
        },
      }}
    >
      Lets Chat
    </Button>
  );

  const getHighlightPoints = () => {
    if (weeklyPoints.length > 0) {
      return weeklyPoints.map((point, i) => (
        <Stack
          key={`${point.substring(0, 4)}-${i}`}
          component="ul"
          margin={0}
          sx={{
            paddingLeft: { xs: 0, sm: "2.5rem" },
            listStylePosition: { xs: "inside", sm: "outside" },
          }}
        >
          <Typography
            component="li"
            variant="cardBodyRegular"
            color={theme.palette.secondary.main}
          >
            {point}
          </Typography>
        </Stack>
      ));
    }

    const points: { header: string; content: string }[] = [];
    if (conversationState === "error" && issueSpotlight) {
      points.push(
        { header: "Issue", content: issueSpotlight.issue },
        { header: "Impact", content: issueSpotlight.impact },
        { header: "Action", content: issueSpotlight.action }
      );
    } else if (conversationState === "success" && winSpotlight) {
      points.push(
        {
          header: "Achievement",
          content: winSpotlight.achievement,
        },
        { header: "Impact", content: winSpotlight.impact },
        {
          header: "Recognition",
          content: winSpotlight.recognition,
        }
      );
    }

    return points.map((point) => (
      <Stack
        key={point.header}
        component="ul"
        margin={0}
        sx={{
          paddingLeft: { xs: 0, sm: "2.5rem" },
          listStylePosition: { xs: "inside", sm: "outside" },
        }}
      >
        <Typography
          component="li"
          variant="cardBodyRegular"
          color={theme.palette.secondary.main}
        >
          <Box component="span" fontWeight="bold">
            {point.header}:{" "}
          </Box>
          {point.content}
        </Typography>
      </Stack>
    ));
  };

  return (
    <Card
      elevation={0}
      className="border border-teamApp-stripe-mid"
      sx={{
        paddingLeft: "1rem",
        paddingRight: "1rem",
        boxShadow: "none",
        background: !expanded
          ? "var(--team-app-gradient-stripe-light)"
          : "repeating-linear-gradient(-45deg, white 0px 5px, var(--team-app-stripe-light) 5px 6px)",
      }}
    >
      <CardHeader
        disableTypography
        sx={{
          background: "none",
          "& .MuiCardHeader-content": {
            minWidth: 0,
          },
        }}
        title={
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            alignItems={{
              xs: "flex-start",
              sm: "start",
            }}
            rowGap="1rem"
            columnGap="1rem"
          >
            <AvatarCircle
              userAccountId={userAccountId}
              name={personName}
              size="large"
            />
            <Box
              display={{
                xs: "none",
                sm: "block",
              }}
            >
              <LargeCustomIcon icon="SEPERATOR" stroke="var(--team-app-divider-subtle)" />
            </Box>
            <Stack
              flex={{
                xs: "0 0 auto",
                sm: "1 1 0",
              }}
              width={{
                xs: "100%",
                sm: "auto",
              }}
              minWidth={0}
              direction="column"
              rowGap=".25rem"
            >
              <Stack
                direction="row"
                flexWrap="wrap"
                rowGap=".25rem"
                columnGap=".5rem"
              >
                <Typography variant="cardBodyHeader" className="text-sapphire-150">
                  {personName}
                </Typography>
                <Chip
                  variant={conversationState}
                  label={label2Variant[conversationState]}
                />
              </Stack>
              <Typography variant="cardBodyRegular">
                {`"${comment}"`}
              </Typography>
            </Stack>
          </Stack>
        }
        action={
          <IconButton
            sx={{
              border: "none",
            }}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <MediumCustomIcon
              icon="CHEVRONUP"
              stroke={theme.palette.secondary.light}
              rotated={!expanded}
            />
          </IconButton>
        }
      />
      <Collapse in={expanded}>
        <CardContent
          sx={{
            marginTop: "1rem",
            marginBottom: "1rem",
            borderRadius: "1rem",
            padding: "1.5rem",
            background:
              "repeating-linear-gradient(-45deg, var(--sapphire-05) 0px 5px, var(--team-app-insights-bg) 5px 6px)",
          }}
        >
          <Stack direction="column" marginBottom="1.5rem">
            <Stack direction="row" marginBottom=".25rem">
              <Typography variant="cardBodyHeader" flex="1 1 0">
                {weeklyPoints.length > 0
                  ? `Week ahead for ${personName}`
                  : `${personName}'s Highlights`}
              </Typography>
              <Box display={{ xs: "none", sm: "block" }}>{letsChatButton}</Box>
            </Stack>
            {getHighlightPoints()}
          </Stack>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            flexWrap="wrap"
            rowGap=".5rem"
            columnGap=".5rem"
          >
            <Button
              variant="contained"
              onClick={() => setIsVisible(() => true)}
              startIcon={
                <MediumCustomIcon
                  icon="DOC"
                  stroke={theme.palette.primary.contrastText}
                />
              }
            >
              Open 1:1 Prep
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={!emailAddress}
              onClick={() => emailAddress && openTeamsChat(emailAddress)}
              startIcon={
                <SmallCustomIcon
                  icon="MESSAGE"
                  className="w-4 h-4"
                />
              }
            >
              Message {personName}
            </Button>
            <Box
              display={{
                xs: "block",
                sm: "none",
              }}
              width="100%"
            >
              {letsChatButton}
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
      {isVisible && (
        <OneOnOnePrepModal
          id={`conversation-card-${userAccountId}-${date}`}
          isVisible
          userAccountId={userAccountId}
          handleClose={() => setIsVisible(() => false)}
          date={date}
        />
      )}
    </Card>
  );
};
